import type React from "react";
import { TableCell, TableRow, IconButton, Box, Typography } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import Table from "../common/Table";
import UserAvatar from "../common/UserAvatar";
import { TestId } from "../../constants/testIds";
import type { OperationsTeam } from "../../types/operationsTeam";
import type { User } from "../../types/user";

type TranslationKeys = keyof typeof translations.en;

interface OperationsTeamTableProps {
  teams: OperationsTeam[];
  users: User[];
  loading?: boolean;
  canEdit?: boolean;
  onEdit?: (team: OperationsTeam) => void;
}

const columns = [
  { id: "name", label: "Name" },
  { id: "members", label: "Team Members" },
  { id: "outstandingBalance", label: "Outstanding Saldo" },
  { id: "actions", label: "Actions", align: "center" as const },
];

const formatOutstandingBalance = (value: number): string => {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(value);
};

const OperationsTeamTable: React.FC<OperationsTeamTableProps> = ({
  teams,
  users,
  loading,
  canEdit = false,
  onEdit,
}) => {
  const { t } = useTranslation();

  const resolveMember = (userId: string): User | undefined => {
    return users.find((user) => user._id === userId);
  };

  return (
    <Table
      data={teams}
      columns={columns.map((column) => ({
        ...column,
        label: t(column.label as TranslationKeys),
      }))}
      loading={loading}
      showSearch={false}
      testId={TestId.OPS_TEAM.TABLE}
      renderRow={(team) => (
        <TableRow key={team._id} data-testid={TestId.OPS_TEAM.ROW}>
          <TableCell>{team.name}</TableCell>
          <TableCell>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {team.userIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              ) : (
                team.userIds.map((userId) => {
                  const member = resolveMember(userId);
                  return (
                    <UserAvatar
                      key={userId}
                      user={member || { _id: userId, name: userId }}
                      size={28}
                      tooltipText={member?.name || userId}
                    />
                  );
                })
              )}
            </Box>
          </TableCell>
          <TableCell data-testid={TestId.OPS_TEAM.OUTSTANDING}>
            {formatOutstandingBalance(team.outstandingBalance)}
          </TableCell>
          <TableCell align="center">
            {canEdit && onEdit && (
              <IconButton size="small" onClick={() => onEdit(team)} color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
      )}
    />
  );
};

export default OperationsTeamTable;
